/**
 * La portée : où une note s'écrit, et quelle place il faut pour l'écrire.
 *
 * Pur : ni React, ni DOM, ni transport. Le dessin est dans `score-layout.ts`,
 * qui choisit seul combien de pixels vaut un temps ; ce qui est ici est le
 * placement vertical, où il se teste. Même partage que `rhythm.ts` et
 * `frieze.ts`.
 *
 * ## Pourquoi une portée, alors que le rail avait deux voies
 *
 * Les voies disaient la hauteur en **relatif** : `laneOf` normalisait l'ambitus
 * de la fenêtre entre zéro et un, et le trait montait ou descendait dans sa
 * bande. C'était lisible et c'était faux dès qu'on comparait deux fenêtres :
 * la même note ne tombait pas au même endroit selon ce qui l'entourait, et
 * l'octave ne se voyait pas du tout.
 *
 * Une portée pose l'échelle une fois pour toutes. Une note est à la place que
 * son nom lui donne, la même du début à la fin du niveau, et l'intervalle
 * redevient une distance qu'on peut mesurer à l'œil.
 *
 * ## Le degré diatonique, et non le demi-ton
 *
 * L'axe compte des **lettres**, pas des demi-tons : `si♭` et `si` sont à la
 * même hauteur sur le papier, parce qu'ils portent la même lettre. C'est la
 * convention de la portée, et elle n'est pas gratuite : elle est ce qui fait
 * qu'une tierce a toujours la même taille à l'œil, quelle que soit la
 * tonalité.
 *
 * Son prix est exactement l'altération, que l'axe ne peut pas dire : `si♭` et
 * `si` sont sur la même ligne. C'est l'armure qui la porte pour les notes de la
 * tonalité, et un signe gravé devant la note pour les autres — la règle entière
 * est dans `keysignature.ts`, et ce module ne fournit que le `alter` qu'elle
 * compare au sien.
 *
 * Les **noms** la disent aussi, dans le chiffrage au-dessus de la portée droite
 * et sur la plaque posée sur chaque touche de la gauche. Ils l'ont longtemps
 * dite seuls, et c'est ce qui interdisait de graver une clé : une portée dont
 * l'altération n'est pas écrite ne se lit pas, et lui poser une clé aurait
 * promis le contraire. Depuis que le signe est là, les noms ne portent plus
 * rien d'indispensable, et un réglage les retire.
 *
 * ## L'orthographe vient de l'accord, comme partout ailleurs
 *
 * `si♭` sous un `Gm` et `la♯` sous un `B` sont la même touche et deux notes
 * différentes, sur deux lignes différentes. C'est la fondamentale de la fenêtre
 * qui tranche, exactement comme pour les noms de `hand-names.ts` : deux façons
 * d'écrire une note sur un seul écran feraient douter de ce que l'écran
 * affirme.
 */

import { windowToneName } from "@/lib/music/theory-helpers";

/** Les lettres dans l'ordre de la portée, et le demi-ton de chacune. */
const LETTERS = "CDEFGAB";
const LETTER_SEMITONES = [0, 2, 4, 5, 7, 9, 11] as const;

/** Une note écrite : sa touche, son nom, et la ligne où elle se pose. */
export type StaffKey = {
  readonly midi: number;
  /**
   * Le nom de la note, en lettres de théorie et sans octave.
   *
   * Dans l'orthographe de la fenêtre, donc `Bb` ou `A#` selon l'accord. C'est
   * la notation du joueur qui le mettra en solfège, et c'est `frieze.ts` qui
   * l'applique : ce module ne connaît pas les réglages.
   */
  readonly name: string;
  /**
   * Le degré diatonique absolu, do central (midi 60) valant 35.
   *
   * Absolu et non relatif à une portée : c'est ce qui permet de comparer les
   * deux mains, de choisir l'étendue d'un niveau et de poser les lignes
   * supplémentaires sans jamais savoir de quelle clé on parle.
   */
  readonly step: number;
  /**
   * L'altération de la note, de `-2` à `2`, que le degré ne peut pas dire.
   *
   * Le degré compte des lettres : `si♭` et `si` sont sur la même ligne. C'est
   * ce nombre-là qui les sépare, et c'est lui que l'armure compare au sien
   * pour décider s'il faut graver un signe. Voir `keysignature.ts`.
   */
  readonly alter: number;
};

/** Une portée : ses cinq lignes, données par la plus grave. */
export type Staff = {
  /** Le degré de la ligne du bas. */
  readonly bottomStep: number;
};

export const STAFF_LINES = 5;

/**
 * Les deux portées du piano, à leur place habituelle.
 *
 * Mi4 en bas pour la clé de sol, sol2 en bas pour la clé de fa : ce sont les
 * portées de n'importe quelle partition de piano, et les garder telles quelles
 * est ce qui fait qu'un joueur qui lit déjà y retrouve ses repères. La clé se
 * grave dans la marge — voir `legendSigns` dans `score-layout.ts` — et la
 * géométrie, elle, est celle du solfège.
 */
export const TREBLE: Staff = { bottomStep: stepOfNatural(64) };
export const BASS: Staff = { bottomStep: stepOfNatural(43) };

/** Le degré de la ligne du haut d'une portée. */
export function topStep(staff: Staff): number {
  return staff.bottomStep + (STAFF_LINES - 1) * 2;
}

/** Les degrés des cinq lignes, de la plus grave à la plus aiguë. */
export function lineSteps(staff: Staff): readonly number[] {
  return Array.from({ length: STAFF_LINES }, (_, rank) => staff.bottomStep + rank * 2);
}

/**
 * Une touche, écrite dans l'orthographe de la fenêtre qui la porte.
 *
 * La règle est celle de `windowToneName`, et c'est la même que `bassOf` dans
 * `hand-names.ts` parce que c'est littéralement la même fonction : le nom
 * écrit sur le rail et la ligne sur laquelle la note se pose doivent venir de
 * la même orthographe, sinon l'écran écrit `si♭` sur la ligne du `la`.
 */
export function spellStaffKey({
  midi,
  spelled = null,
  rootSpelling,
  rootPitchClass,
  chordPitchClasses = null,
  keyTonic = null,
}: {
  readonly midi: number;
  /** L'orthographe de l'analyseur pour cette note. Voir `windowToneName`. */
  readonly spelled?: string | null;
  /** L'orthographe de la fondamentale, qui décide des bémols et des dièses. */
  readonly rootSpelling: string;
  readonly rootPitchClass: number;
  /** Les classes de hauteur que l'accord énonce. Voir `windowToneName`. */
  readonly chordPitchClasses?: ReadonlySet<number> | null;
  /** L'orthographe de la tonique du morceau. Voir `windowToneName`. */
  readonly keyTonic?: string | null;
}): StaffKey {
  const pitchClass = classOf(midi);
  const written = windowToneName({
    spelled,
    pitchClass,
    rootSpelling,
    rootPitchClass,
    chordPitchClasses,
    keyTonic,
  });

  const letter = LETTERS.indexOf(written.slice(0, 1).toUpperCase());

  if (letter < 0) {
    // Une orthographe qu'aucun des deux helpers ne sait produire ; on ne peut
    // pas la placer, et la placer au hasard serait pire que la placer bas.
    return { midi, name: written, step: stepOfNatural(midi), alter: 0 };
  }

  const alter = count(written, "#") - count(written, "b");

  return {
    midi,
    name: written,
    alter,
    // La note sonne `alter` demi-tons au-dessus de sa naturelle : c'est cette
    // naturelle-là dont on cherche l'octave, sinon `si♯3` se placerait sur le
    // `do` de l'octave au-dessus, qui est pourtant la même touche.
    step: Math.round((midi - alter - LETTER_SEMITONES[letter]) / 12) * 7 + letter,
  };
}

/**
 * L'étendue à graver pour une main : ses notes, et sa portée quoi qu'il arrive.
 *
 * La portée entière même quand rien ne l'atteint : cinq lignes qui
 * apparaîtraient et disparaîtraient selon ce que la main joue ne seraient plus
 * une échelle, et l'écran changerait de forme d'un niveau à l'autre sans que
 * rien de musical n'ait bougé.
 */
export function staffSpan(steps: readonly number[], staff: Staff): { readonly low: number; readonly high: number } {
  return {
    low: Math.min(staff.bottomStep, ...steps),
    high: Math.max(topStep(staff), ...steps),
  };
}

/**
 * La taille d'un degré, en pixels, pour que les portées tiennent dans la place.
 *
 * C'est le seul endroit de ce module qui touche à des pixels, et c'est
 * volontaire : le choix est une arithmétique, pas un dessin, et il décide de
 * ce qui est lisible. Le laisser dans le composant en ferait la seule règle de
 * la frise qu'on ne peut pas vérifier.
 *
 * On prend la plus grande taille qui tient, et à défaut la plus petite qu'on
 * s'autorise : une portée qui déborde de quelques pixels reste lisible, une
 * portée dont on aurait coupé les notes ne l'est pas. C'est le seul arbitrage
 * possible ici, puisque l'étendue vient du morceau et non d'un réglage.
 */
export function fitStep({
  spans,
  budget,
  sizes,
}: {
  /** Les étendues à graver, en degrés, une par portée. */
  readonly spans: readonly { readonly low: number; readonly high: number }[];
  /** La place disponible pour l'empilement des degrés, en pixels. */
  readonly budget: number;
  /** Les tailles admises, de la plus grande à la plus petite. */
  readonly sizes: readonly number[];
}): number {
  const gaps = spans.reduce((total, span) => total + Math.max(span.high - span.low, 0), 0);
  const smallest = sizes[sizes.length - 1] ?? 1;

  if (gaps <= 0) {
    return sizes[0] ?? smallest;
  }

  return sizes.find((size) => gaps * size <= budget) ?? smallest;
}

/**
 * Les lignes supplémentaires d'une note, hors de sa portée.
 *
 * Les degrés **pairs** par rapport à la ligne du bas, c'est-à-dire les lignes
 * et non les interlignes, de la portée jusqu'à la note incluse quand elle est
 * sur une ligne. Vide tant que la note est dans la portée.
 */
export function ledgerSteps(step: number, staff: Staff): number[] {
  const top = topStep(staff);
  const out: number[] = [];

  for (let at = top + 2; at <= step; at += 2) {
    out.push(at);
  }

  for (let at = staff.bottomStep - 2; at >= step; at -= 2) {
    out.push(at);
  }

  return out;
}

/** Le degré d'une touche prise pour naturelle, quand on n'a pas d'orthographe. */
function stepOfNatural(midi: number): number {
  const pitchClass = classOf(midi);
  const letter = LETTER_SEMITONES.findIndex((semitone) => semitone >= pitchClass);
  const at = letter < 0 ? LETTER_SEMITONES.length - 1 : letter;

  return Math.floor(midi / 12) * 7 + at;
}

function count(text: string, sign: string): number {
  return text.split(sign).length - 1;
}

function classOf(midi: number): number {
  return ((midi % 12) + 12) % 12;
}
